/**
 * Custom hook for managing attention (Atención) state and logic
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import api, { atencionesAPI } from '../../utils/api';
import { toast } from 'sonner';

export const useAtencion = (requerimiento, open) => {
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [atencion, setAtencion] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [inspeccionActaEstado, setInspeccionActaEstado] = useState(null);
  const [platformConfig, setPlatformConfig] = useState({
    company_name: 'IMAGINA',
    platform_name: 'Sistema de Postventa',
    logo_url: null
  });
  const actaInputRef = useRef(null);
  const [sealData, setSealData] = useState(null);
  const [sealLoading, setSealLoading] = useState(false);
  
  // Form state
  const [testigoTipo, setTestigoTipo] = useState('solicitante');
  const [testigo, setTestigo] = useState({
    nombre: '',
    dni: '',
    telefono: '',
    email: ''
  });
  const [datosAtencion, setDatosAtencion] = useState({
    fecha: new Date().toISOString().split('T')[0],
    horaInicio: '',
    horaFin: '',
    actaInspeccionUrl: '',
    actaAtencionUrl: '',
    actaEstado: ''
  });
  
  // Acta diaria state
  const [showActaDiariaDialog, setShowActaDiariaDialog] = useState(false);
  const [selectedSolicitudForActa, setSelectedSolicitudForActa] = useState(null);
  const [editingActaId, setEditingActaId] = useState(null);
  
  // Preview
  const [previewImage, setPreviewImage] = useState(null);
  
  // Track saved solucion_aplicada values (to know what's persisted in DB)
  const [savedSoluciones, setSavedSoluciones] = useState({});
  
  // Track which solicitud is being terminated
  const [terminatingSolicitudId, setTerminatingSolicitudId] = useState(null);

  // Load platform config
  const loadPlatformConfig = useCallback(async () => {
    try {
      const response = await api.get('/configuracion/general');
      setPlatformConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  }, []);

  // Initialize testigo from requerimiento
  const initializeTestigo = useCallback((tipo) => {
    if (!requerimiento) return;
    
    if (tipo === 'solicitante') {
      setTestigo({
        nombre: requerimiento.solicitante_nombre || requerimiento.cliente_nombre || '',
        dni: '',
        telefono: requerimiento.solicitante_telefono || requerimiento.cliente_telefono || '',
        email: requerimiento.solicitante_email || requerimiento.cliente_email || ''
      });
    } else if (tipo === 'titular') {
      setTestigo({
        nombre: requerimiento.cliente_nombre || '',
        dni: '',
        telefono: requerimiento.cliente_telefono || '',
        email: requerimiento.cliente_email || ''
      });
    }
  }, [requerimiento]);

  // Load solicitudes for an atencion
  const loadSolicitudes = useCallback(async (atencionId) => {
    try {
      setLoadingSolicitudes(true);
      const response = await atencionesAPI.getSolicitudes(atencionId);
      const solicitudesData = response.data || [];
      setSolicitudes(solicitudesData);
      
      // Initialize saved soluciones from DB values
      const savedValues = {};
      solicitudesData.forEach(sol => {
        if (sol.solucion_aplicada && sol.solucion_aplicada.trim() !== '') {
          savedValues[sol.id] = sol.solucion_aplicada;
        }
      });
      setSavedSoluciones(savedValues);
    } catch (error) {
      console.error('Error loading solicitudes:', error);
    } finally {
      setLoadingSolicitudes(false);
    }
  }, []);

  // Create new atencion
  const createAtencion = useCallback(async () => {
    if (!requerimiento) return;
    
    try {
      initializeTestigo('solicitante');
      
      const response = await atencionesAPI.create({
        requerimiento_id: requerimiento.id,
        testigo_tipo: 'solicitante',
        testigo_nombre: requerimiento.solicitante_nombre || requerimiento.cliente_nombre,
        testigo_dni: '',
        testigo_telefono: requerimiento.solicitante_telefono || requerimiento.cliente_telefono,
        testigo_email: requerimiento.solicitante_email || requerimiento.cliente_email,
        fecha_atencion: new Date().toISOString().split('T')[0],
        hora_inicio: new Date().toTimeString().slice(0, 5)
      });
      
      setAtencion(response.data);
      
      // Always try to load inspection data using requerimiento_id
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let actaInspeccionUrl = '';
      
      try {
        const inspResponse = await api.get(`/inspecciones/${requerimiento.id}`);
        if (inspResponse.data) {
          // Get inspeccion_id from inspection response
          const inspeccionId = inspResponse.data.id;
          if (inspeccionId) {
            // Priority: If acta_url exists (PDF uploaded), use it directly
            // Otherwise, use the HTML view endpoint
            if (inspResponse.data.acta_url) {
              actaInspeccionUrl = inspResponse.data.acta_url;
            } else {
              actaInspeccionUrl = `${backendUrl}/api/inspecciones/${inspeccionId}/acta-view`;
            }
          }
          // Set inspection acta_estado
          if (inspResponse.data.acta_estado) {
            setInspeccionActaEstado(inspResponse.data.acta_estado);
          }
        }
      } catch (e) {
        console.error('Error loading inspection on create:', e);
      }
      
      setDatosAtencion(prev => ({
        ...prev,
        horaInicio: new Date().toTimeString().slice(0, 5),
        actaInspeccionUrl: actaInspeccionUrl
      }));
      
      await loadSolicitudes(response.data.id);
    } catch (error) {
      toast.error('Error al crear la atención');
    }
  }, [requerimiento, initializeTestigo, loadSolicitudes]);

  // Load existing atencion
  const loadAtencion = useCallback(async () => {
    if (!requerimiento?.id) return;
    
    setLoading(true);
    try {
      const response = await atencionesAPI.get(requerimiento.id);
      if (response.data) {
        setAtencion(response.data);
        setTestigoTipo(response.data.testigo_tipo || 'solicitante');
        setTestigo({
          nombre: response.data.testigo_nombre || '',
          dni: response.data.testigo_dni || '',
          telefono: response.data.testigo_telefono || '',
          email: response.data.testigo_email || ''
        });
        
        // Build acta URLs dynamically using current backend URL
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Always try to load inspection data using requerimiento_id
        let actaInspeccionUrl = '';
        let inspeccionIdFromResponse = response.data.inspeccion_id;
        
        try {
          const inspResponse = await api.get(`/inspecciones/${requerimiento.id}`);
          if (inspResponse.data) {
            if (!inspeccionIdFromResponse && inspResponse.data.id) {
              inspeccionIdFromResponse = inspResponse.data.id;
            }
            if (inspeccionIdFromResponse) {
              // Priority: If acta_url exists (PDF uploaded), use it directly
              // Otherwise, use the HTML view endpoint
              if (inspResponse.data.acta_url) {
                actaInspeccionUrl = inspResponse.data.acta_url;
              } else {
                actaInspeccionUrl = `${backendUrl}/api/inspecciones/${inspeccionIdFromResponse}/acta-view`;
              }
            }
            if (inspResponse.data.acta_estado) {
              setInspeccionActaEstado(inspResponse.data.acta_estado);
            }
          }
        } catch (e) {
          console.error('Error loading inspection:', e);
        }
        
        // For atencion acta - determine URL based on how it was created:
        // - If acta_atencion_url exists (PDF uploaded), use that URL directly
        // - If only acta_estado exists (sent by email), use acta-view HTML
        let actaAtencionUrl = '';
        if (response.data.acta_atencion_url) {
          // PDF was uploaded manually - use direct URL
          actaAtencionUrl = response.data.acta_atencion_url;
        } else if (response.data.acta_estado) {
          // Acta was sent by email - use HTML view
          actaAtencionUrl = `${backendUrl}/api/atenciones/${response.data.id}/acta-view`;
        }
        
        setDatosAtencion({
          fecha: response.data.fecha_atencion || new Date().toISOString().split('T')[0],
          horaInicio: response.data.hora_inicio || '',
          horaFin: response.data.hora_fin || '',
          actaInspeccionUrl: actaInspeccionUrl,
          actaAtencionUrl: actaAtencionUrl,
          actaEstado: response.data.acta_estado || ''
        });
        
        // Fetch seal data if acta is approved
        const acta_estado = response.data.acta_estado || '';
        if (acta_estado.startsWith('aprobado') && response.data.id) {
          setSealLoading(true);
          try {
            const sealResp = await api.get(`/seal/atencion/${response.data.id}`);
            if (sealResp.data?.aprobado) {
              setSealData(sealResp.data);
            }
          } catch (sealErr) {
            console.warn('No se pudo obtener seal data:', sealErr);
          } finally {
            setSealLoading(false);
          }
        } else {
          setSealData(null);
        }
        
        await loadSolicitudes(response.data.id);
      } else {
        await createAtencion();
      }
    } catch (error) {
      await createAtencion();
    } finally {
      setLoading(false);
    }
  }, [requerimiento?.id, loadSolicitudes, createAtencion]);

  // Silent refresh - only updates estado/acta_estado without resetting form
  const silentRefresh = useCallback(async () => {
    if (!atencion?.id) return;
    try {
      const response = await atencionesAPI.get(requerimiento.id);
      if (response.data) {
        const newEstado = response.data.estado;
        const newActaEstado = response.data.acta_estado || '';
        const oldActaEstado = datosAtencion.actaEstado;

        if (newActaEstado !== oldActaEstado || newEstado !== atencion.estado) {
          setAtencion(prev => ({ ...prev, estado: newEstado, acta_estado: newActaEstado }));
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          let actaAtencionUrl = '';
          if (response.data.acta_atencion_url) {
            actaAtencionUrl = response.data.acta_atencion_url;
          } else if (newActaEstado) {
            actaAtencionUrl = `${backendUrl}/api/atenciones/${response.data.id}/acta-view`;
          }
          setDatosAtencion(prev => ({
            ...prev,
            actaEstado: newActaEstado,
            actaAtencionUrl: actaAtencionUrl || prev.actaAtencionUrl
          }));
          await loadSolicitudes(atencion.id);
        }
      }
    } catch (e) {
      // Silent - no toast on error
    }
  }, [atencion?.id, atencion?.estado, requerimiento?.id, datosAtencion.actaEstado, loadSolicitudes]);

  // Polling + visibilitychange when approval is pending
  useEffect(() => {
    const isPending = datosAtencion.actaEstado === 'enviado_correo' || datosAtencion.actaEstado === 'enviado_whatsapp';
    if (!isPending || !open) return;

    const interval = setInterval(silentRefresh, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') silentRefresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [datosAtencion.actaEstado, open, silentRefresh]);

  // Handle testigo type change
  const handleTestigoTipoChange = useCallback((value) => {
    setTestigoTipo(value);
    initializeTestigo(value);
  }, [initializeTestigo]);

  // Save atencion
  const handleSave = useCallback(async (onUpdate) => {
    if (!atencion) return;
    
    setLoading(true);
    try {
      await atencionesAPI.update(atencion.id, {
        testigo_tipo: testigoTipo,
        testigo_nombre: testigo.nombre,
        testigo_dni: testigo.dni,
        testigo_telefono: testigo.telefono,
        testigo_email: testigo.email,
        fecha_atencion: datosAtencion.fecha,
        hora_inicio: datosAtencion.horaInicio
      });
      
      // Save each solicitud's solucion_aplicada
      const newSavedSoluciones = { ...savedSoluciones };
      for (const sol of solicitudes) {
        if (sol.solucion_aplicada !== undefined) {
          await atencionesAPI.updateSolicitud(atencion.id, sol.id, {
            solucion_aplicada: sol.solucion_aplicada
          });
          if (sol.solucion_aplicada && sol.solucion_aplicada.trim() !== '') {
            newSavedSoluciones[sol.id] = sol.solucion_aplicada;
          } else {
            delete newSavedSoluciones[sol.id];
          }
        }
      }
      setSavedSoluciones(newSavedSoluciones);
      
      // Update atencion state with saved testigo data
      setAtencion(prev => ({
        ...prev,
        testigo_tipo: testigoTipo,
        testigo_nombre: testigo.nombre,
        testigo_dni: testigo.dni,
        testigo_telefono: testigo.telefono,
        testigo_email: testigo.email
      }));
      
      toast.success('Atención guardada correctamente');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Error al guardar la atención');
    } finally {
      setLoading(false);
    }
  }, [atencion, testigoTipo, testigo, datosAtencion, solicitudes, savedSoluciones]);

  // Upload acta
  const handleActaUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !atencion) return;

    // Reset the input to allow uploading the same file again
    e.target.value = '';

    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    try {
      toast.loading('Subiendo acta...', { id: 'acta-upload' });
      const response = await atencionesAPI.uploadActa(atencion.id, file);
      // Use the actual PDF URL returned from the upload, not the acta-view HTML
      const pdfUrl = response.data?.acta_url;
      
      if (pdfUrl) {
        setDatosAtencion(prev => ({ 
          ...prev, 
          actaAtencionUrl: pdfUrl,
          actaEstado: 'adjuntado'
        }));
        toast.success('Acta subida correctamente', { id: 'acta-upload' });
      } else {
        toast.error('Error: No se recibió la URL del archivo', { id: 'acta-upload' });
      }
    } catch (error) {
      toast.error('Error al subir el acta', { id: 'acta-upload' });
    }
  }, [atencion]);

  // Delete acta
  const handleActaDelete = useCallback(async () => {
    if (!atencion) return;
    
    try {
      await atencionesAPI.deleteActa(atencion.id);
      setDatosAtencion(prev => ({ 
        ...prev, 
        actaAtencionUrl: '',
        actaEstado: ''
      }));
      toast.success('Acta eliminada');
    } catch (error) {
      toast.error('Error al eliminar el acta');
    }
  }, [atencion]);

  // Terminar solicitud (con NPS si habilitado)
  const handleTerminarSolicitud = useCallback(async (solicitudAtencionId) => {
    if (!atencion) return null;
    
    setTerminatingSolicitudId(solicitudAtencionId);
    try {
      const response = await atencionesAPI.terminarSolicitud(atencion.id, solicitudAtencionId);
      setSolicitudes(prev => 
        prev.map(s => s.id === solicitudAtencionId ? { ...s, estado: 'terminado' } : s)
      );
      toast.success('Solicitud terminada');
      return response.data || {};
    } catch (error) {
      toast.error('Error al terminar la solicitud');
      return null;
    } finally {
      setTerminatingSolicitudId(null);
    }
  }, [atencion]);

  // Acta diaria handlers
  const handleOpenActaDiaria = useCallback((solicitud) => {
    setSelectedSolicitudForActa(solicitud);
    setEditingActaId(null);
    setShowActaDiariaDialog(true);
  }, []);

  const handleOpenActaDiariaEdit = useCallback((acta, solicitud) => {
    setSelectedSolicitudForActa(solicitud);
    setEditingActaId(acta.id);
    setShowActaDiariaDialog(true);
  }, []);

  const handleActaDiariaCreated = useCallback(async (actaData) => {
    if (atencion) {
      await loadSolicitudes(atencion.id);
    }
    setEditingActaId(actaData.id);
  }, [atencion, loadSolicitudes]);

  const handleActaDiariaUpdated = useCallback(async () => {
    if (atencion) {
      await loadSolicitudes(atencion.id);
    }
  }, [atencion, loadSolicitudes]);

  const closeActaDiariaDialog = useCallback(() => {
    setShowActaDiariaDialog(false);
    setEditingActaId(null);
  }, []);

  // Update solicitud solucion
  const updateSolicitudSolucion = useCallback((solicitudId, value) => {
    setSolicitudes(prev => 
      prev.map(s => s.id === solicitudId ? { ...s, solucion_aplicada: value } : s)
    );
  }, []);

  // Send email
  const handleSendEmail = useCallback(async (onUpdate) => {
    if (!atencion) return;
    
    if (solicitudes.some(s => s.estado !== 'terminado')) {
      toast.error('Todas las solicitudes deben estar terminadas para enviar por correo');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await atencionesAPI.enviarCorreo(atencion.id);
      
      if (response.data?.status === 'success') {
        // Generate URL dynamically using current backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        setDatosAtencion(prev => ({ 
          ...prev, 
          actaAtencionUrl: `${backendUrl}/api/atenciones/${atencion.id}/acta-view`,
          actaEstado: 'enviado_correo'
        }));
        toast.success('Correo enviado correctamente al testigo.');
      } else {
        toast.error(response.data?.message || 'Error al enviar el correo');
      }
      
      if (onUpdate) onUpdate();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al enviar el correo');
      if (error.response?.status === 400 && detail) {
        if (detail.includes('aprobada')) {
          setDatosAtencion(prev => ({ ...prev, actaEstado: 'aprobado_correo' }));
        } else if (detail.includes('rechazada')) {
          setDatosAtencion(prev => ({ ...prev, actaEstado: 'rechazado_correo' }));
        }
      }
    } finally {
      setSendingEmail(false);
    }
  }, [atencion, solicitudes]);

  // Send WhatsApp
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  
  const handleSendWhatsApp = useCallback(async (onUpdate) => {
    if (!atencion) return;
    
    if (solicitudes.some(s => s.estado !== 'terminado')) {
      toast.error('Todas las solicitudes deben estar terminadas para enviar por WhatsApp');
      return;
    }

    if (!testigo.telefono) {
      toast.error('El testigo no tiene teléfono registrado. No se puede enviar por WhatsApp.');
      return;
    }

    setSendingWhatsapp(true);
    try {
      const response = await atencionesAPI.enviarWhatsapp(atencion.id);
      
      if (response.data?.success) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        setDatosAtencion(prev => ({ 
          ...prev, 
          actaAtencionUrl: `${backendUrl}/api/atenciones/${atencion.id}/acta-view`,
          actaEstado: 'enviado_whatsapp'
        }));
        
        if (response.data.sendpulse_success) {
          toast.success(`WhatsApp enviado a ${response.data.destinatario} (${response.data.telefono})`);
        } else {
          toast.warning(`Acta registrada como enviada, pero SendPulse reportó un error. Contacte al administrador.`);
        }
      } else {
        toast.error(response.data?.message || 'Error al enviar por WhatsApp');
      }
      
      if (onUpdate) onUpdate();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al enviar por WhatsApp');
    } finally {
      setSendingWhatsapp(false);
    }
  }, [atencion, solicitudes, testigo.telefono]);

  // Terminar atencion
  const handleTerminar = useCallback(async (onUpdate, onClose) => {
    if (!atencion) return;
    
    if (solicitudes.some(s => s.estado !== 'terminado')) {
      toast.error('Todas las solicitudes deben estar terminadas');
      return;
    }
    
    if (!datosAtencion.actaAtencionUrl && !['aprobado_correo', 'aprobado_whatsapp'].includes(datosAtencion.actaEstado)) {
      toast.error('El acta de atención debe estar adjuntada o aprobada');
      return;
    }

    setLoading(true);
    try {
      await atencionesAPI.terminar(atencion.id);
      toast.success('Atención finalizada. Estado actualizado a "Terminado"');
      if (onUpdate) onUpdate();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al terminar la atención');
    } finally {
      setLoading(false);
    }
  }, [atencion, solicitudes, datosAtencion]);

  // Validation helpers
  const allSolicitudesTerminado = solicitudes.length > 0 && solicitudes.every(s => s.estado === 'terminado');
  const canSendEmail = allSolicitudesTerminado;
  const canPrint = allSolicitudesTerminado;
  const canTerminar = datosAtencion.actaEstado !== 'enviado_correo' && datosAtencion.actaEstado !== 'enviado_whatsapp' && allSolicitudesTerminado && (datosAtencion.actaAtencionUrl || ['aprobado_correo', 'aprobado_whatsapp'].includes(datosAtencion.actaEstado));

  // Initialize on dialog open
  useEffect(() => {
    if (open && requerimiento) {
      loadPlatformConfig();
      loadAtencion();
    }
  }, [open, requerimiento, loadPlatformConfig, loadAtencion]);

  const fetchUltimoTestigo = useCallback(async () => {
    if (!requerimiento?.proyecto_id) return;
    setMagicLoading(true);
    try {
      const params = new URLSearchParams({ proyecto_id: requerimiento.proyecto_id });
      if (requerimiento.etapa) params.append('etapa', requerimiento.etapa);
      if (requerimiento.departamento) params.append('departamento', requerimiento.departamento);
      const response = await api.get(`/inspecciones/testigo/ultimo?${params}`);
      const data = response.data;
      if (data) {
        setTestigo({
          nombre: data.testigo_nombre || '',
          dni: data.testigo_dni || '',
          telefono: data.testigo_telefono || '',
          email: data.testigo_email || ''
        });
        toast.success('Datos copiados del último testigo');
      } else {
        toast.warning('No se encontraron datos previos de testigo para este proyecto, etapa y departamento');
      }
    } catch {
      toast.error('Error al buscar datos del último testigo');
    } finally {
      setMagicLoading(false);
    }
  }, [requerimiento?.proyecto_id, requerimiento?.etapa, requerimiento?.departamento]);

  return {
    // State
    loading,
    sendingEmail,
    atencion,
    solicitudes,
    loadingSolicitudes,
    inspeccionActaEstado,
    platformConfig,
    actaInputRef,
    testigoTipo,
    testigo,
    datosAtencion,
    savedSoluciones,
    previewImage,
    
    // Acta diaria state
    showActaDiariaDialog,
    selectedSolicitudForActa,
    editingActaId,
    terminatingSolicitudId,
    
    // Setters
    setTestigo,
    setDatosAtencion,
    setPreviewImage,
    
    // Actions
    handleTestigoTipoChange,
    handleSave,
    handleActaUpload,
    handleActaDelete,
    handleTerminarSolicitud,
    handleOpenActaDiaria,
    handleOpenActaDiariaEdit,
    handleActaDiariaCreated,
    handleActaDiariaUpdated,
    closeActaDiariaDialog,
    updateSolicitudSolucion,
    handleSendEmail,
    handleSendWhatsApp,
    handleTerminar,
    fetchUltimoTestigo,
    magicLoading,
    sendingWhatsapp,
    
    // Validation
    allSolicitudesTerminado,
    canSendEmail,
    canPrint,
    canTerminar,

    // NPS config
    npsHabilitado: !!platformConfig?.nps_habilitado,
    
    // Seal data
    sealData,
    sealLoading
  };
};

export default useAtencion;
